import {
  type RecursPeriod,
  dueOn,
  recursDaysLabel,
  resolveSchedule,
} from '@goaltree/backend/convex/lib/schedule'
import type { TFunction } from 'i18next'

export const localDateKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Whether a recurrence is scheduled on the local calendar day of `d`. */
export const dueOnDate = (
  period: string | null | undefined,
  recurrenceDay: number | null | undefined,
  d: Date,
  recursDays?: readonly number[] | null,
) => dueOn(period, recurrenceDay, localDateKey(d), recursDays)

// A running fast stays due even on a day its schedule skips (multi-day fasts);
// an idle one-off fast is never due, it waits in Everyday.
export const habitDueOn = (
  h: {
    fast?: unknown
    oneOff?: boolean
    recursPeriod?: string | null
    recurrenceDay?: number | null
    recursDays?: readonly number[] | null
  },
  d: Date,
) => !!h.fast || (!h.oneOff && dueOnDate(h.recursPeriod, h.recurrenceDay, d, h.recursDays))

/** "Daily" / "Weekday" / … or "Mon · Wed · Fri" for a specific-days schedule. */
export function periodLabel(
  t: TFunction,
  locale: string,
  period: string | null | undefined,
  recursDays?: readonly number[] | null,
): string {
  const p = period ?? 'daily'
  return p === 'days' && recursDays?.length
    ? recursDaysLabel(recursDays, locale)
    : t(`inbox.period.${p}`)
}

/** Optimistic recursPeriod/recursDays for an updateRecursPeriod call (mirrors the server). */
export function optimisticSchedule(
  args: { recursPeriod?: RecursPeriod; recursDays?: number[] },
  prevDays?: readonly number[] | null,
): { recursPeriod?: RecursPeriod; recursDays?: number[]; recurrenceDay?: number } {
  if (!args.recursPeriod) return { recursPeriod: undefined, recursDays: undefined }
  return resolveSchedule(args.recursPeriod, args.recursDays ?? prevDays)
}

type ScheduleFields = {
  recursPeriod?: string | null
  recursDays?: number[] | null
  recurrenceDay?: number | null
}

/** Patch a cached task/habit with an updateRecursPeriod call; keeps the period when none is sent. */
export function patchSchedule<T extends ScheduleFields>(
  prev: T,
  args: { recursPeriod?: RecursPeriod; recursDays?: number[]; recurrenceDay?: number },
): Pick<T, keyof ScheduleFields> {
  if (!args.recursPeriod)
    return { ...prev, recurrenceDay: args.recurrenceDay ?? prev.recurrenceDay } as T
  const s = resolveSchedule(args.recursPeriod, args.recursDays ?? prev.recursDays)
  return {
    recursPeriod: s.recursPeriod,
    recursDays: s.recursDays ?? (prev.recursDays === null ? null : undefined),
    recurrenceDay: s.recurrenceDay ?? args.recurrenceDay ?? prev.recurrenceDay,
  } as T
}
